import { useAtomValue } from "jotai";
import { forwardRef } from "react";
import type { SVGProps } from "react";
import { themeAtom } from "@src/jotai/theme";
import * as DarkIcons from "./dark/index";
import * as LightIcons from "./light/index";
import * as CommonIcons from "./common/index";

// 主题图标组件的 props 类型，支持反转参数
interface ThemedIconProps {
  /** 反转主题：当为 true 时，暗黑模式显示亮色图标，亮色模式显示暗色图标 */
  invert?: boolean;
}

// 创建主题感知的图标组件（用于主题相关的图标）
const createThemedIcon = (iconName: keyof typeof LightIcons) => {
  return ({ invert = false, ...props }: ThemedIconProps) => {
    const theme = useAtomValue(themeAtom);
    // 根据 invert 参数决定是否反转主题
    const isDark = invert ? theme !== "dark" : theme === "dark";
    const IconComponent = isDark ? (DarkIcons as any)[iconName] : (LightIcons as any)[iconName];
    if (!IconComponent) return null;
    return <IconComponent {...props} />;
  };
};

// 创建通用图标组件（不随主题变化的图标）
const createCommonIcon = (iconName: keyof typeof CommonIcons) => {
  const CommonIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>((props, ref) => {
    const IconComponent = CommonIcons[iconName];
    return <IconComponent ref={ref} {...props} />;
  });

  CommonIcon.displayName = String(iconName);
  return CommonIcon;
};

// 自动遍历 LightIcons 并创建所有主题感知的图标组件
const themedIconNames = Object.keys(LightIcons) as Array<keyof typeof LightIcons>;
const themedIcons = themedIconNames.reduce((acc, iconName) => {
  acc[iconName] = createThemedIcon(iconName);
  return acc;
}, {} as any);

// 自动遍历 CommonIcons 并创建所有通用图标组件
const commonIconNames = Object.keys(CommonIcons) as Array<keyof typeof CommonIcons>;
const commonIcons = commonIconNames.reduce((acc, iconName) => {
  acc[iconName] = createCommonIcon(iconName);
  return acc;
}, {} as any);

// 命名导出所有图标（自动解构）
export const {
  // 通用图标（从 common 目录）
  IconBack,
  IconEmpty,
  IconError,
  IconErrorRed,
  IconHelp,
  IconQuestion,
  IconInfo,
  IconLock,
  IconLockWhite,
  IconLocation,
  IconModalClose,
  IconModalCloseGrey,
  IconMore,
  IconPc,
  IconRight,
  IconScrollRight,
  IconSelect,
  IconSelected,
  IconArrowRight,
  IconAd,
  IconCredit,
  IconAiGradient,
  IconAiLinear,
  IconDownloadBlue,
  IconCollected,
  IconScriptBlack,
  IconNoDownload,
  IconStoryboardScript,
  IconSuccess,
  IconSuccessGreen,
  IconArrowBlue,
  IconArrowBlack,
  IconArrowBlueFull,
  IconArrowGreyFull,
  IconRank,
  IconRankGray,
  IconStepError,
  IconStepCheck,
  IconDrag,
  IconLogoClip,
  IconLogoBoost,
  IconLogoKalodata,
  IconClipShort,
  IconKalodataShort,
  IconImageEmpty,
  IconFileEmpty,
  IconVideoEmpty,
  IconFolderColour,
  IconPublishPosted,
  IconPublishScheduled,
  IconBatchDelete,
  IconExpand,
  IconLogoPilot,
  // 主题相关的图标（从 light/dark 目录）
  IconCamera,
  IconStep1,
  IconStep2,
  IconStep3,
  IconStep4,
  IconStep5,
  IconStep6,
  IconStep7,
  IconStep8,
  IconStep9,
  IconStep10,
  IconStep11,
  IconStep12,
  IconStep13,
  IconStep14,
  IconStep15,
  IconStep16,
  IconLogo,
  IconLogo2,
  IconStepReload,
  IconDelete,
  IconAdd,
  IconSearch,
  IconTriangleArrow,
  IconArrow,
  IconFolder,
  IconShare,
  IconCollect,
  IconDownload,
  IconLink,
  IconStepLoading,
  IconEmail,
  IconPhone,
  IconApple,
  IconTiktok,
  IconGoogle,
  IconWechat,
  IconLanguage,
  IconArrowDown,
  IconHome,
  IconLocked,
  IconCode,
  IconPortrait,
  IconSun,
  IconMoon,
  IconMoonFull,
  IconMenuPic,
  IconAdd2,
  IconDirection,
  IconTime,
  IconCountDown,
  IconLogo3,
  IconLogoShort3,
  IconNotice,
  IconClose,
  IconToggle,
  IconSwitch,
  IconOut,
  IconPage,
  IconProduct,
  IconVideo,
  IconScript,
  IconIdea,
  IconVideoFull,
  IconScriptFull,
  IconIdeaFull,
  IconSocialPublisher,
  IconSocialPublisherFull,
  IconFolderFull,
  IconInputDel,
  IconChangeLang,
  IconSetting,
  IconTranslate,
  IconLanguage2,
  IconCopy,
  IconAttention,
  IconLoading,
  IconVideoRemake,
  IconVideoRemakeActive,
} = { ...themedIcons, ...commonIcons } as any;
